import React, { useEffect, useState } from "react";
import { TabType, ReportStructure, DetailCaseComponentAnalysis } from "./types";
import SubHeader from "./SubHeader";
import TabNavigation from "./TabNavigation";
import OverallPerformance from "./Tabs/Overall/OverallPerformance";
import DetailedCaseAnalysisTab from "./Tabs/DetailedAnalysisTab/DetailedCaseAnalysis";
import DetailsTab from "./Tabs/DetailsTab/DetailsTab";
import { useSearchParams } from "react-router-dom";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import {
    DetailedCaseData,
    QuestionType,
    CaseQuestionData,
} from "./Tabs/DetailsTab/types";
import { formatISODateString } from "src/utils/timeUtils";
import {
    useFetchDetailedReport,
    useFetchGeneralReport,
} from "src/hooks/useFetchReport";
import {
    useFetchCases,
    BackendCase,
    CaseQuestion,
} from "src/hooks/useFetchCases";

import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "src/api/firebase";
import { useFetchInterviews } from "src/hooks/useFetchInterviews";

const ReportPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>("overall");
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("interviewSessionId");
    const { interviews, isLoading: isLoadingInterviews } = useFetchInterviews();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(1);

    // Find the current interview and get its caseProblemId
    const currentInterview = interviews.find(
        (interview) => interview.id === sessionId
    );
    const caseId = currentInterview?.caseProblemId;

    // Fetch report data
    const { data: overallReportData, isLoading: isLoadingOverall } =
        useFetchGeneralReport(sessionId || "");

    const { data: detailedReportData, isLoading: isLoadingDetailed } =
        useFetchDetailedReport(sessionId || "");

    // Get the raw response from getCaseProblems
    const { data: rawCaseData, isLoading: isLoadingCase } = useQuery<{
        data: BackendCase[];
    }>({
        queryKey: ["raw_cases", caseId],
        queryFn: async () => {
            const getCaseProblems = httpsCallable(functions, "getCaseProblems");
            const response = await getCaseProblems({
                ids: caseId ? [caseId] : undefined,
            });
            return response.data as { data: BackendCase[] };
        },
        enabled: Boolean(caseId) && !isLoadingInterviews,
    });

    // Process case data for the DetailsTab
    const caseContext = rawCaseData?.data?.[0]
        ? {
              heading: "Case Context",
              content: rawCaseData.data[0].caseContext?.promptContext || "",
          }
        : { heading: "Case Context", content: "" };

    const caseProblem = rawCaseData?.data?.[0]
        ? {
              heading: "Problem Statement",
              content: rawCaseData.data[0].caseContext?.promptQuestion || "",
          }
        : { heading: "Problem Statement", content: "" };

    // Transform case questions into the required format
    const transformQuestionData = (): Record<
        QuestionType,
        CaseQuestionData
    > => {
        if (
            !rawCaseData?.data?.[0]?.caseQuestions ||
            rawCaseData.data[0].caseQuestions.length === 0
        ) {
            return {};
        }

        const caseQuestions = rawCaseData.data[0].caseQuestions;

        const questionData: Record<QuestionType, CaseQuestionData> = {};
        const questionTypes: Record<string, number> = {};

        caseQuestions.forEach((question: CaseQuestion, index: number) => {
            let baseQuestionType: string;

            switch (question.type) {
                case "structure":
                    baseQuestionType = "opening_and_structure";
                    break;
                case "brainstorm":
                    baseQuestionType = "brainstorming";
                    break;
                case "math":
                    baseQuestionType = "quantitative_analysis";
                    break;
                case "recommendation":
                    baseQuestionType = "final_recommendation";
                    break;
                default:
                    console.warn(`Unknown question type: ${question.type}`);
                    return;
            }

            let questionType = baseQuestionType;
            if (questionTypes[baseQuestionType]) {
                questionType = `${baseQuestionType}_${questionTypes[baseQuestionType]}`;
            }
            questionTypes[baseQuestionType] =
                (questionTypes[baseQuestionType] || 0) + 1;

            const availableInfo: string[] = [];

            if (question.additionalInformation) {
                availableInfo.push(
                    ...question.additionalInformation.map(
                        (info) => info.content
                    )
                );
            }

            const graphs: Array<{ graph_url: string; graph_title: string }> =
                [];
            if (question.basicInformation) {
                question.basicInformation.forEach((info) => {
                    if (info.graphUrl && info.graphTitle) {
                        graphs.push({
                            graph_url: info.graphUrl,
                            graph_title: info.graphTitle,
                        });
                    }
                });
            }

            questionData[questionType] = {
                question: question.question,
                case_question_number: index + 1,
                availableInformation: availableInfo,
                graphs,
                questionType: baseQuestionType,
            };
        });

        return questionData;
    };

    const questionData = transformQuestionData();

    // Common data from reportData.metadata
    const commonData = {
        timeSpent: (() => {
            const minutes = overallReportData?.metadata?.time?.minutes;
            const minutesNum =
                typeof minutes === "string" ? parseInt(minutes, 10) : minutes;
            if (!minutesNum || minutesNum <= 0 || isNaN(minutesNum))
                return "Not available";
            return `${minutes} mins`;
        })(),
        date: overallReportData?.metadata?.date
            ? formatISODateString(overallReportData.metadata.date)
            : "N/A",
        caseName: overallReportData?.metadata?.title ?? "N/A",
    };

    if (!sessionId) {
        return <div>Invalid session id</div>;
    }

    if (isLoadingInterviews || !currentInterview) {
        return <div>Loading interview data...</div>;
    }

    // Switch between tabs
    const renderTabContent = () => {
        switch (activeTab) {
            case "overall":
                return (
                    <OverallPerformance
                        performanceSummary={
                            overallReportData?.performance_summary
                        }
                        peerComparison={overallReportData?.peer_comparison}
                        competencyScores={overallReportData?.competency_scores}
                        componentAnalysis={
                            overallReportData?.component_analysis
                        }
                    />
                );
            case "detailed":
                return (
                    <DetailsTab
                        sessionId={sessionId}
                        caseId={caseId}
                        caseContext={caseContext}
                        caseProblem={caseProblem}
                        questionData={questionData}
                        improvedResponses={{}}
                        currentQuestionIndex={currentQuestionIndex}
                        onQuestionChange={(index) => {
                            setCurrentQuestionIndex(index);
                        }}
                        isLoading={
                            isLoadingOverall ||
                            isLoadingDetailed ||
                            isLoadingCase
                        }
                    />
                );
            case "analysis":
                return detailedReportData ? (
                    <DetailedCaseAnalysisTab
                        data={detailedReportData}
                        componentAnalysis={
                            overallReportData?.component_analysis
                        }
                    />
                ) : null;
            // case "feedback":
            //   return <InterviewRecordingsPlayer sessionId={sessionId} />;
            default:
                return (
                    <OverallPerformance
                        performanceSummary={
                            overallReportData?.performance_summary
                        }
                        peerComparison={overallReportData?.peer_comparison}
                        competencyScores={overallReportData?.competency_scores}
                        componentAnalysis={
                            overallReportData?.component_analysis
                        }
                    />
                );
        }
    };
    console.log(overallReportData?.component_analysis);

    return (
        <div className="min-h-screen bg-[#F8F9FA]">
            <div className="w-full">
                <SubHeader
                    timeSpent={commonData.timeSpent}
                    date={commonData.date}
                    caseName={commonData.caseName}
                />

                <TabNavigation
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                <div className="px-6 py-4">{renderTabContent()}</div>
            </div>
        </div>
    );
};

export default ReportPage;
